import first from "lodash/first";
import isEqual from "lodash/isEqual";

import { ScheduleAnalyticsFieldName } from "@/constants/analytics/Scheduling";
import type { MixpanelProps } from "@/services/Analytics";

const AnalyticsUserRoles: Record<AnalyticsUserRole, AnalyticsUserRole> = {
  Patient: "Patient",
  Support: "Support",
};

const getChanges = ({
  current,
  previous,
}: {
  current: MixpanelProps;
  previous: MixpanelProps;
}) => {
  const changes: MixpanelProps = {};
  // Only processes current items:
  // * For items that are not present in the previous object, they are considered new
  // * For items that are present in the previous object, but are different, they are considered changed
  // * For items that are present in the previous object, but not in the current object, they are considered unchanged:
  //   * Without this, every property would be logged in the event, regardless of whether it had changed
  Object.getOwnPropertyNames(current).forEach((key: string) => {
    if (isEqual(current[key], previous[key])) return;
    changes[`${key}__old`] = previous[key];
    changes[`${key}__new`] = current[key];
  });
  return changes;
};

const getUserRole = ({
  isSupport,
}: {
  isSupport: boolean;
}): AnalyticsUserRole => {
  return isSupport ? AnalyticsUserRoles.Support : AnalyticsUserRoles.Patient;
};

const mapFullProfileToUserInfo = (
  user: FullProfileResponse,
): AnalyticsUserInfo => {
  const universalStatusCode = getPrimaryUserUniversalStatusCode(user);
  const linkedPatientIds = user.isPatient
    ? undefined
    : user.linkedPatients?.map((patient) => patient.externalId);
  return {
    adminEpisodeStatus: user.adminEpisodeStatus,
    gender: user.gender,
    linkedPatientIds,
    numberOfLinkedPatients: user.linkedPatients?.length,
    numberOfSupports: user.supportedBy?.length,
    universalStatusCode,
  };
};

const getPrimaryUserUniversalStatusCode = (
  user: FullProfileResponse,
): undefined | UniversalStatusCode => {
  if (user.isPatient) {
    return user.universalStatusCode;
  }
  const primaryPatient = first(user.linkedPatients);
  if (primaryPatient) {
    return primaryPatient.universalStatusCode;
  }
  return undefined;
};

const getActorField = ({
  appUserExternalId,
  isAppUserSupport,
}: {
  appUserExternalId?: string;
  isAppUserSupport: boolean;
}) => {
  if (!appUserExternalId) return {};
  return {
    [isAppUserSupport
      ? ScheduleAnalyticsFieldName.SupportExternalId
      : ScheduleAnalyticsFieldName.PatientExternalId]: appUserExternalId,
  };
};

export {
  getActorField,
  getChanges,
  getPrimaryUserUniversalStatusCode,
  getUserRole,
  mapFullProfileToUserInfo,
};
